import type { ReactNode } from "react"
import React, { useCallback, useEffect, useState } from "react"
import type { FileRejection } from "react-dropzone"
import { useDropzone } from "react-dropzone"
import { ErrorCode } from "react-dropzone"
import { AiFillFilePdf, AiFillFileZip } from "react-icons/ai"
import { BsCloudUpload, BsFiletypePsd } from "react-icons/bs"

import type { CenterProps, StackProps } from "@chakra-ui/react"
import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"

import { Modal, useToast } from "@supliful/common"

import { GearIcon } from "../elements/icons/GearIcon"
import { useImageResize } from "../lib/hooks/useImageResize"
import { useS3Upload } from "../lib/hooks/useS3Upload"

export type FileUploaderProps = Omit<StackProps, "onSubmit" | "onDrop"> & {
  path: string
  onSubmit: (
    key: string,
    image: {
      file: File
      preview: string
    },
    uploadedFile: {
      fileUrl: string
    },
  ) => void
  getSignedS3Url: (key: string, fileType: string) => Promise<string>
  button?: ReactNode
  contextMenuItems?: ReactNode
  accept?: string
  modalTitle?: string
  modalClosable?: boolean
  layout?: "horizontal" | "vertical"
}

const icons: { [fileType: string]: (...args: any) => React.JSX.Element } = {
  "application/zip": AiFillFileZip,
  "application/pdf": AiFillFilePdf,
  "image/vnd.adobe.photoshop": BsFiletypePsd,
}

type FileUploaderEmptyViewProps = CenterProps

export const FileUploaderEmptyView = ({
  ...rest
}: FileUploaderEmptyViewProps) => {
  return (
    <Center
      w="full"
      h="full"
      p={2}
      textAlign="center"
      border="2px dashed"
      borderColor="neutrals.200"
      _hover={{ background: "neutrals.100" }}
      {...rest}
    >
      Drag and drop or tap to upload
    </Center>
  )
}

const FilePreview = ({ file }: { file: File }) => {
  return (
    <Flex w="full" justify="center" p={6} align="center">
      {icons[file.type] && (
        <Box as={icons[file.type]} boxSize="4rem" color="main.700" />
      )}

      <Text wordBreak="break-word" maxW="full">
        {file.name}
      </Text>
    </Flex>
  )
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  children,
  path,
  onSubmit,
  getSignedS3Url,
  contextMenuItems,
  button,
  accept,
  modalTitle,
  modalClosable = true,
  layout = "horizontal",
  ...rest
}) => {
  const modalProps = useDisclosure()
  const [image, setImage] = useState<{
    file: File
    preview: string
  } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const toast = useToast()

  const handleOnDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length) {
        const code = rejected[0].errors?.[0].code

        toast({
          status: "error",
          // Only display title if it is a custom error code.
          title:
            Object.values<string>(ErrorCode).includes(code) || !code
              ? null
              : code,
          description:
            rejected[0].errors?.[0].message ||
            "File not accepted. Please choose another file",
        })
      }

      // No files passed validation. This can happen when a file is dropped instead of using the OS file picker.
      if (!accepted.length) return

      setImage({
        file: accepted[0],
        preview: URL.createObjectURL(accepted[0]),
      })
      modalProps.onOpen()
    },
    [modalProps],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleOnDrop,
    accept,
  })
  const { css: _, ...rootProps } = getRootProps() // css not compatible with chakra
  const [upload, { loading }] = useS3Upload({
    path: path,
    getSignedS3Url,
  })
  const [resize, { loading: resizeInProgress }] = useImageResize()

  const handleSubmitImage = async () => {
    let uploadedFile

    // Resize JPG or PNG images larger than 3MB in size.
    const shouldResizeImage = (file: File): boolean =>
      file.size > 3 * 1024 * 1024 &&
      ["image/jpeg", "image/jpg", "image/png"].includes(file.type)

    if (!image || !image.file) return

    if (shouldResizeImage(image.file)) {
      const resizedImage = await resize(image.file)

      if (!resizedImage) {
        return
      }

      uploadedFile = await upload(resizedImage)
    } else {
      uploadedFile = await upload(image.file)
    }

    if (!uploadedFile) return

    onSubmit(uploadedFile.fileKey, image, { fileUrl: uploadedFile.fileUrl })
    handleClose()
  }

  const handleClose = () => {
    modalProps.onClose()
    setImage(null)
  }

  const handleRemoveFile = React.useCallback(() => {
    if (image) URL.revokeObjectURL(image.preview)
  }, [image])

  useEffect(() => handleRemoveFile, [handleRemoveFile])

  return (
    <>
      <Stack
        {...rootProps}
        direction={
          layout === "horizontal" ? { base: "column", md: "row" } : "column"
        }
        align="center"
        justify={{ base: "center", md: "flex-start" }}
        cursor="pointer"
        position="relative"
        w="100%"
        spacing={layout === "horizontal" ? 0 : 2}
        style={{ overflow: "visible" }}
        {...rest}
      >
        <input {...getInputProps()} />

        {children && (
          <Box
            w="full"
            h="full"
            position="relative"
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={() => setIsDragging(false)}
            {...(isDragging && {
              _before: {
                content: "''",
                position: "absolute",
                top: 0,
                left: 0,
                width: "full",
                height: "full",
                border: "2px dashed white",
                boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                background: "blackAlpha.200",
                zIndex: "overlay",
              },
            })}
          >
            {children}
          </Box>
        )}

        {contextMenuItems ? (
          <Menu>
            <MenuButton
              as={IconButton}
              style={{
                position: "absolute",
                top: -4,
                right: -4,
              }}
              size="sm"
              icon={<GearIcon />}
              colorScheme="tertiary"
              aria-label="File context menu"
              onClick={(e) => e.stopPropagation()}
            />
            <MenuList>{contextMenuItems}</MenuList>
          </Menu>
        ) : (
          button || (
            <IconButton
              style={{
                position: "absolute",
                top: -4,
                right: -4,
              }}
              icon={<BsCloudUpload />}
              size="sm"
              colorScheme="tertiary"
              aria-label="Upload file"
            />
          )
        )}
      </Stack>

      <Modal
        {...modalProps}
        closeOnEsc={modalClosable}
        closeOnOverlayClick={modalClosable}
        showCloseButton={modalClosable}
        onClose={handleClose}
        title={modalTitle || "Upload new file"}
        actions={
          <>
            <Button
              colorScheme="tertiary"
              size="md"
              isDisabled={loading}
              onClick={handleClose}
              mr={4}
            >
              Cancel
            </Button>

            <Button
              colorScheme="primary"
              size="md"
              isLoading={loading || resizeInProgress}
              isDisabled={loading || resizeInProgress}
              onClick={handleSubmitImage}
            >
              Submit
            </Button>
          </>
        }
      >
        {accept && image?.file && <FilePreview file={image.file} />}

        {!accept && image && (
          <Box background="white">
            <Image
              objectFit="contain"
              w="100%"
              p={12}
              maxH="400px"
              src={image.preview}
              alt="upload preview"
            />
          </Box>
        )}
      </Modal>
    </>
  )
}
